import type { Runner, Session } from '../api/types'
import type { EngineSettings } from '../transcript/agentSettings'
import { type Choice, choiceLabel, choices, concrete, currentEffort, effortOption, modelChoices } from './choices'
import { agentLabel, agentOf } from './format'

/** A session's own effort, by the name its agent gives it; null when it reports none. */
function sessionEffort(session: Session, runners: readonly Runner[] | null): string | null {
  const adapter = agentOf(session, runners)?.agent
  const settings = session.settings
  if (!settings) return null
  const offered = choices(effortOption(settings.options, adapter)?.choices)
  // An agent that offers an effort but reports no current value is at its default (Claude Code, through ACP).
  return choiceLabel(offered, currentEffort(settings, adapter)?.value) ?? (offered.length > 0 ? 'Default' : null)
}

/** The worker's runtime settings; the runner catalogue supplies no defaults. */
export function workerSettings(worker: Session, runners: readonly Runner[] | null) {
  const settings = worker.settings
  const model = settings ? choiceLabel(modelChoices(settings), settings.current_model) : agentLabel(worker, runners).model
  return {model, effort: sessionEffort(worker, runners)}
}

const words = (text: string) => text.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean)

/**
 * A model's name in an agent's catalogue: the entry whose value is the id (an alias or a pinned id), else the named
 * entry whose name's words are the id's last words, the longest if several (`Haiku 5.5` for `x-haiku-5-5`, and not
 * `Haiku 5` nor `Haiku 4.5`); null when none is. Taken from the catalogue alone, whatever its vendor.
 */
export function catalogueName(catalogue: readonly Choice[], id: string): string | null {
  const exact = catalogue.find(choice => choice.id === id)
  if (exact) return exact.label
  const said = words(id)
  let best: string[] = []
  let name: string | null = null
  for (const choice of catalogue) {
    if (choice.label === choice.id) continue
    const own = words(choice.label)
    if (own.length > best.length && own.length <= said.length && own.every((word, at) => word === said[said.length - own.length + at])) {
      best = own
      name = choice.label
    }
  }
  return name
}

/** The models the session's agent names: the session's own choices, then its runner's catalogue. */
export function catalogueOf(session: Session, runners: readonly Runner[] | null): Choice[] {
  const catalogue = agentOf(session, runners)?.catalogue
  return [
    ...(session.settings ? modelChoices(session.settings) : []),
    ...choices(catalogue?.options?.find(option => option.category === 'model')?.choices),
    ...Object.entries(catalogue?.model_details ?? {}).flatMap(([id, detail]) => concrete(detail.name) ? [{id, label: detail.name}] : []),
  ]
}

/**
 * An engine child's settings as people read them: its model by its agent's catalogue name, else as reported; its own
 * effort, else its lead's, which it runs at (a parent child's `leadEffort`, else the session's), else none shown.
 */
export function engineSettings(settings: EngineSettings | undefined, session: Session, runners: readonly Runner[] | null, leadEffort?: string) {
  const model = concrete(settings?.model) ? catalogueName(catalogueOf(session, runners), settings.model) ?? settings.model : null
  const lead = leadEffort ?? sessionEffort(session, runners)
  const effort = concrete(settings?.effort) ? settings.effort : lead ? `${lead}, as its lead` : null
  return {model, effort, effortOptional: true}
}

/** A subagent's model and effort; an optional effort that is not reported is left out. */
export function SubAgentSettings({model, effort, effortOptional = false}: {model?: string | null; effort?: string | null; effortOptional?: boolean}) {
  const shown = concrete(effort) ? effort : effortOptional ? null : 'Not reported'
  return <span className="code-subagent-settings"><span>Model: {concrete(model) ? model : 'Not reported'}</span>{shown !== null && <>{' · '}<span>Effort: {shown}</span></>}</span>
}
